import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import { FlightShopType, TripCategory } from "redmond";
import {
  setDepartureDate,
  setDestination,
  setOrigin,
  setReturnDate,
  setTripCategory,
} from "../../search/actions/actions";
import {
  getDepartureDate,
  getDestination,
  getOrigin,
  getReturnDate,
  getTripCategory,
} from "../../search/reducer";
import { flightShopTypeSelector, flightsSelector } from "../../shop/reducer";
import {
  setFlights,
  setFlightShopType,
  setTripSummariesError,
  setTripSummariesLoading,
} from "../../shop/actions/actions";
import { useChfarExerciseParams } from "./useChfarExerciseParams";
import { useGetLocationQuery } from "../reducers/location/api";
import { useFlightList } from "./useFlightList";

/**
 * Gets the data from the ChFAR search and store it in the existing Redux store for the flights module
 * @returns {Object} Object containing the loading state of the origin, destination, and flight list, and whether the search is synced
 */
export const useSyncChfarFlights = () => {
  const {
    itineraryId,
    contractId,
    flightShopType,
    tripCategory,
    origin,
    originRegionType,
    departureDate,
    destination,
    destinationRegionType,
    returnDate,
  } = useChfarExerciseParams();

  const {
    isLoading: isOriginLoading,
    isError: isOriginError,
    data: originLocation,
  } = useGetLocationQuery(
    { location: origin as string, type: originRegionType as string },
    {
      skip: !origin || !originRegionType,
    }
  );

  const {
    isLoading: isDestinationLoading,
    isError: isDestinationError,
    data: destinationLocation,
  } = useGetLocationQuery(
    { location: destination as string, type: destinationRegionType as string },
    {
      skip: !destination || !destinationRegionType,
    }
  );

  const {
    data: chfarFlightsList,
    isLoading: isFlightListLoading,
    isFetching: isFlightListFetching,
    isError: isFlightListError,
  } = useFlightList();

  const dispatch = useDispatch();

  // We don't want to sync the store if we're not in the ChFAR exercise flow
  const shouldSkipSync =
    !itineraryId ||
    !contractId ||
    flightShopType !== FlightShopType.CHANGE_FOR_ANY_REASON_EXERCISE;

  useEffect(() => {
    if (shouldSkipSync) {
      return;
    }

    dispatch(setTripCategory(tripCategory));

    dispatch(
      setDepartureDate(departureDate ? dayjs(departureDate).toDate() : null)
    );

    dispatch(setReturnDate(returnDate ? dayjs(returnDate).toDate() : null));

    dispatch(setFlightShopType(FlightShopType.CHANGE_FOR_ANY_REASON_EXERCISE));

    if (originLocation) {
      dispatch(setOrigin(originLocation));
    }

    if (destinationLocation) {
      dispatch(setDestination(destinationLocation));
    }
  }, [
    shouldSkipSync,
    tripCategory,
    setTripCategory,
    setDepartureDate,
    setReturnDate,
    setFlightShopType,
    setOrigin,
    originLocation,
    setDestination,
    destinationLocation,
    dispatch,
    itineraryId,
    contractId,
    origin,
    departureDate,
    originRegionType,
    destination,
    returnDate,
    destinationRegionType,
  ]);

  // This will keep the ChFAR Flight List in sync with the Redux store's existing flight list
  // We could have used a few conditionals for endpoint and data to call and use, but
  // I think the simplest solution is to set the data from our query to the shop store
  useEffect(() => {
    if (shouldSkipSync) {
      return;
    }
    if (isFlightListFetching || isFlightListLoading) {
      dispatch(setTripSummariesLoading());
    }

    if (chfarFlightsList) {
      dispatch(setFlights(chfarFlightsList.flights));
    }

    if (isFlightListError) {
      dispatch(setTripSummariesError());
    }
  }, [
    shouldSkipSync,
    dispatch,
    chfarFlightsList,
    isFlightListFetching,
    isFlightListLoading,
  ]);

  const storedTripCategory = useSelector(getTripCategory);
  const storedFlightShopType = useSelector(flightShopTypeSelector);
  const storedOrigin = useSelector(getOrigin);
  const storedDepartureDate = useSelector(getDepartureDate);
  const storedDestination = useSelector(getDestination);
  const storedReturnDate = useSelector(getReturnDate);
  const flightsList = useSelector(flightsSelector);

  return {
    isLoading: isOriginLoading || isDestinationLoading || isFlightListLoading,
    isError: isOriginError || isDestinationError || isFlightListError,
    isSynced: Boolean(
      storedDepartureDate &&
        (storedReturnDate
          ? storedTripCategory === TripCategory.ROUND_TRIP
          : storedTripCategory === TripCategory.ONE_WAY) &&
        storedFlightShopType ===
          FlightShopType.CHANGE_FOR_ANY_REASON_EXERCISE &&
        storedOrigin &&
        storedDestination &&
        flightsList
    ),
  };
};
